import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// ─── MOBILE HAMBURGER MENU ────────────────────────────────────────────────────
//
//  Renders the dropdown via React portal into document.body so it is never
//  clipped by overflow or repositioned by a CSS transform on any ancestor.
//
//  Usage:
//    <MobileMenu>
//      {(close) => <button onClick={() => { doThing(); close(); }}>...</button>}
//    </MobileMenu>
//

const MobileMenu = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState(null);
  const btnRef          = useRef(null);
  const dropRef         = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (dropRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown',  close);
    document.addEventListener('touchstart', close, { passive: true });
    return () => {
      document.removeEventListener('mousedown',  close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  const toggle = (e) => {
    if (!open) {
      const r = e.currentTarget.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  };

  const dropdown = open && pos ? createPortal(
    <div
      ref={dropRef}
      style={{
        position: 'fixed',
        top:      pos.top,
        right:    pos.right,
        zIndex:   99999,
        background:   'var(--surface)',
        border:       '1px solid var(--border2)',
        borderRadius: 'var(--radius)',
        boxShadow:    '0 8px 24px rgba(0,0,0,0.3)',
        minWidth:     190,
        padding:      '8px 0',
      }}
    >
      {typeof children === 'function' ? children(() => setOpen(false)) : children}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        className="btn-ghost btn-sm"
        onClick={toggle}
        aria-label="Menu"
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, padding: 0 }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          {open
            ? <><line x1="4" y1="4" x2="16" y2="16" /><line x1="16" y1="4" x2="4" y2="16" /></>
            : <><line x1="3" y1="6" x2="17" y2="6" /><line x1="3" y1="10" x2="17" y2="10" /><line x1="3" y1="14" x2="17" y2="14" /></>
          }
        </svg>
      </button>
      {dropdown}
    </>
  );
};

export default MobileMenu;
